import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import RankForm from '../../../RankForm'
import Spinner from '@/common/components/Spinner'
import useQueryGetRankById from '@/modules/manage-rank/api/hooks/useQueryGetRankById'
import { UpdateRankRequest } from '@/modules/manage-rank/type'
import useMutationUpdateRank from '@/modules/manage-rank/api/hooks/useMutateUpdateRank'
import useMutationUpdateRankUpRewards from '@/modules/manage-rank/api/hooks/useMutationUpdateRankUpRewards'
import { useQueryClient } from '@tanstack/react-query'
import { RankQueryKeys } from '@/modules/manage-rank/keys'
import { z } from 'zod'
import { createRankFormSchema } from '../../../RankForm/components'
import { AdminRankUpRewardLineDtoType } from '@/schema'

type RankFormValues = z.infer<typeof createRankFormSchema>

export default function UpdateRankForm() {
	const navigate = useNavigate()
	const { id } = useParams()
	const { data: rank, isLoading } = useQueryGetRankById(id!)
	const queryClient = useQueryClient()

	const { mutateAsync: updateRank } = useMutationUpdateRank()
	const { mutateAsync: updateRankUpRewards } = useMutationUpdateRankUpRewards()

	const handleSubmit = async (values: RankFormValues) => {
		try {
			const { rankUpRewards, allowRepeatRankUpRewards, ...rankData } = values

			await updateRankUpRewards({
				id: id!,
				payload: {
					rewards: (rankUpRewards ?? []).map((r) => ({
						type: r.type,
						amount: r.amount,
						luckyWheelSettingId: r.luckyWheelSettingId ?? undefined,
						gameId: r.gameId ?? undefined,
						betAmount: r.betAmount ?? undefined,
						presetId: r.presetId ?? undefined,
					})),
				},
			})

			await updateRank({
				id: id!,
				payload: {
					...(rankData as UpdateRankRequest),
					allowRepeatRankUpRewards,
				},
			})

			toast.success('แก้ไข Rank สำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [RankQueryKeys.GetRankById, id],
			})
			navigate('/operator/rank')
		} catch (error) {
			showErrorToast(
				error instanceof Error ? error : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
			)
		}
	}

	if (isLoading || !rank) return <Spinner />

	const formDefaultValues: Partial<RankFormValues> = {
		...rank,
		dailyCheckInId: rank.dailyCheckInSettingId,
		allowRepeatRankUpRewards: rank.allowRepeatRankUpRewards ?? false,
		rankUpRewards: (rank.rankUpRewards ?? []).map((r) => ({
			type: r.type as unknown as AdminRankUpRewardLineDtoType,
			amount: r.amount,
			luckyWheelSettingId: r.luckyWheelSettingId ?? null,
			gameId: r.gameId ?? null,
			betAmount: r.betAmount ?? null,
			presetId: r.presetId ?? null,
		})),
	}

	return (
		<RankForm
			mode="edit"
			defaultValues={formDefaultValues}
			onSubmit={handleSubmit}
		/>
	)
}
